import { useEffect, useState } from "react";
import { Printer } from "@phosphor-icons/react";
import client from "../lib/api";

/**
 * BusinessOverviewPage — printable one-pager for banks/lenders.
 * Route: /business-overview (public). Light, print-friendly design.
 * When the owner is logged in, company details auto-fill from their account.
 */
export default function BusinessOverviewPage() {
  const [details, setDetails] = useState({});

  useEffect(() => {
    document.title = "Business Overview | FreightPilot AI";
    (async () => {
      try {
        const [me, prefs] = await Promise.all([
          client.get("/auth/me"),
          client.get("/settings/pricing"),
        ]);
        setDetails({
          legal_name: me.data.company_name || "",
          owner: me.data.full_name || "",
          mc: prefs.data.mc_number || "",
          dot: prefs.data.dot_number || "",
          phone: prefs.data.phone || "",
        });
      } catch {
        /* not logged in — leave blanks for hand-fill */
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-white text-neutral-900" data-testid="business-overview-page">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { margin: 14mm; }
        }
      `}</style>

      {/* Print button */}
      <div className="no-print fixed top-4 right-4 z-10">
        <button
          onClick={() => window.print()}
          data-testid="print-overview-btn"
          className="inline-flex items-center gap-2 rounded-full bg-[#FF5A1F] text-white font-semibold px-5 py-2.5 text-sm shadow-lg hover:bg-[#e64d15] transition-colors"
        >
          <Printer size={16} weight="fill" /> Print / Save as PDF
        </button>
      </div>

      <div className="max-w-[720px] mx-auto px-8 py-10">
        {/* Logo header */}
        <div className="flex items-end justify-between border-b-4 border-[#FF5A1F] pb-4 mb-6">
          <div>
            <div className="font-display text-3xl font-bold tracking-tight">
              Freight<span className="text-[#FF5A1F]">Pilot</span> AI
            </div>
            <div className="text-xs text-neutral-500 mt-1 tracking-widest uppercase">Business Overview</div>
          </div>
          <div className="text-right text-xs text-neutral-500 leading-relaxed">
            freightpilotai.ai<br />contact@freightpilotai.ai
          </div>
        </div>

        {/* Facts strip */}
        <div className="grid grid-cols-3 gap-3 mb-6 text-sm">
          <div className="border border-neutral-200 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Industry</div>
            <div className="font-semibold">Transportation Technology (SaaS)</div>
          </div>
          <div className="border border-neutral-200 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Market</div>
            <div className="font-semibold">U.S. Trucking — Owner-Operators &amp; Small Fleets</div>
          </div>
          <div className="border border-neutral-200 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Revenue Model</div>
            <div className="font-semibold">Recurring Subscriptions from $79/mo</div>
          </div>
        </div>

        {/* What the business does */}
        <h2 className="font-display text-lg font-bold mb-2">What the business does</h2>
        <p className="text-sm leading-relaxed text-neutral-700 mb-5">
          FreightPilot AI is an all-in-one operations platform that helps trucking companies run
          their business from a single dashboard. The average owner-operator spends hours each
          week on paperwork, rate negotiations, and invoicing — FreightPilot automates that work
          with artificial intelligence. The product is live and serving customers at freightpilotai.ai.
        </p>

        {/* Core services */}
        <h2 className="font-display text-lg font-bold mb-2">Core services</h2>
        <div className="space-y-2.5 mb-5 text-sm">
          {[
            ["AI rate negotiation", "Drafts professional rate emails to freight brokers, grounded in real diesel prices, deadhead mileage, and the carrier's own pricing rules."],
            ["Load management", "Captures load postings (including by photo/screenshot), scores them against the carrier's profitability rules, and tracks every bid from offer to booked."],
            ["Dispatch & paperwork", "Dispatch board, document vault, rate confirmations, and compliance records in one place."],
            ["Billing & cash flow", "Automated invoicing, customer credit tracking, and payment reminders so carriers get paid faster."],
            ["Fleet tools", "Driver management, payroll, IFTA mileage reporting, and maintenance tracking."],
          ].map(([title, desc]) => (
            <div key={title} className="flex gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F] mt-2 shrink-0" />
              <div>
                <b>{title}</b> — <span className="text-neutral-700">{desc}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Revenue */}
        <h2 className="font-display text-lg font-bold mb-2">Revenue model</h2>
        <p className="text-sm leading-relaxed text-neutral-700 mb-6">
          Recurring subscriptions — monthly plans from $79/month with discounted annual plans.
          Payments are processed through Stripe. Customers self-serve through a free trial and
          convert to paid plans; the platform also drives customer acquisition through social
          media advertising and a referral program.
        </p>

        {/* Company details — fill-in block */}
        <div className="border-2 border-neutral-300 rounded-xl p-5 mb-6">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-3 font-bold">Company details</div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2.5 text-sm" data-testid="company-details-grid">
            <div><span className="text-neutral-500">Legal name:</span> {details.legal_name ? <b>{details.legal_name}</b> : "______________________"}</div>
            <div><span className="text-neutral-500">Owner:</span> {details.owner ? <b>{details.owner}</b> : "Mike Gee"}</div>
            <div><span className="text-neutral-500">MC #:</span> {details.mc ? <b>{details.mc}</b> : "______________________"}</div>
            <div><span className="text-neutral-500">USDOT #:</span> {details.dot ? <b>{details.dot}</b> : "______________________"}</div>
            <div><span className="text-neutral-500">Years in business:</span> ________________</div>
            <div><span className="text-neutral-500">Phone:</span> {details.phone ? <b>{details.phone}</b> : "______________________"}</div>
          </div>
        </div>

        <div className="text-xs text-neutral-400 text-center">
          FreightPilot AI · https://freightpilotai.ai · contact@freightpilotai.ai
        </div>
      </div>
    </div>
  );
}
